import type { BundleSummary, DataFlag, SignedStep, SigningPayload } from "../api/types.js";
import { clock, dateTime, formatMoney, lastDigits, proxyTail, sumMoney } from "../lib/format.js";
import type { IconId } from "../lib/icon.js";
import { UNLOCK_TAKES, UNLOCK_TITLE } from "./security-model.js";

/**
 * What a signed step says in words — from the step alone (moved out of the card
 * in run 2 so the notices can name a step without importing a view). Names as
 * signed (`nickname`, `registered_name`, the registry's masked proxy), amounts as
 * the payload's decimal strings; the only arithmetic is `leaves`, a sum of the
 * payload's own amounts per account in integer minor units — and only for a /1
 * payload: a /2 payload signs those sums itself (`signedLeaves`).
 */
const PROXY: Readonly<Record<string, string>> = {
  mobile: "PayNow mobile",
  nric_fin: "PayNow NRIC/FIN",
  uen: "PayNow UEN",
  // DCTA-120 (§F.2 6g): a wallet is a "PayNow address" to a customer — never "VPA".
  vpa: "PayNow address",
  account_number: "Account",
};

function money(step: SignedStep): { value: string; currency: string } | undefined {
  if (step.type === "securities_order") return step.cash_amount;
  if (step.type === "protective_action") return step.new_limit;
  if (step.type === "payee_add") return undefined;
  return step.amount;
}

/**
 * DCTA-219: the release time the bank SIGNED for a held step — a hold signed beside a token step too (outcome
 * `step_up_required` with `hold_until`: policy 1.8.0 asks Mdm Lim's officer story at the question level AND holds it AND
 * steps it up). Never a time the app works out.
 */
export function signedHold(step: SignedStep): string | undefined {
  const o = step.policy.outcome;
  return o === "hold_until" || o === "step_up_required" ? step.policy.hold_until : undefined;
}

function timing(step: SignedStep, now: string): string {
  const until = signedHold(step);
  if (until !== undefined) return `goes out ${dateTime(until)}`;
  if (step.policy.outcome === "step_up_required") return "goes out once your token code is in";
  return now;
}

export interface Row {
  readonly ic: IconId;
  readonly title: string;
  readonly sub: string;
  readonly detail: string;
  readonly amount: string;
  readonly name: string;
  /** A computed amount's derivation, the bank's signed sentence verbatim; absent for a fixed amount. */
  readonly derivation?: string;
  /** DCTA-136 (F10): the strings on this row the bank flagged, printed in their inert form (`Inert`). */
  readonly inert?: readonly Inert[];
}

/**
 * DCTA-136 (phrase map F10, on DCTA-135's contract): a string on this row the bank's firewall flagged
 * (`firewall.data_flags`) — a poisoned bill reference, a biller's name, a nickname. It is the record's text and it is
 * signed as it is, so it is never rewritten; the card prints the bank's INERT form in its place wherever it would have
 * printed it (`display`: quoted, invisible characters gone, cut — the bank's, never re-cut here), in the data face and
 * isolated (bankcard.ts), and says the bank's sentence beside it (`notice`). A flag from a bank older than the inert
 * form has no `display`: the string is then quoted where it stands, never printed bare.
 */
export interface Inert {
  readonly display: string;
  readonly notice?: string;
  /**
   * DCTA-143 (Q13: shown = signed): the string as the payload signs it — never rewritten, never cut. The bank's
   * `display` is a visual cut of it; "Show the full text" on the card prints this, every character accounted for.
   */
  readonly full: string;
  /** DCTA-143: which string it is on its step — "bill_reference", "biller/name", "payee/nickname" … (`inert.fieldLabel`). */
  readonly field: string;
  readonly stepId: string;
}

/** A flag's field on its step: the pointer past `/steps/N/` — "bill_reference", "biller/name", "payee/nickname". */
const fieldOf = (pointer: string): string => pointer.replace(/^\/steps\/\d+\//, "");

/** DCTA-143: a flag's Inert entry for the string it flags (`value`, as signed) — the bank's display, else the whole string quoted. */
export function inertOf(flag: DataFlag, value: string): Inert {
  return {
    display: flag.display ?? `“${value}”`,
    ...(flag.notice === undefined ? {} : { notice: flag.notice }),
    full: value,
    field: fieldOf(flag.field),
    stepId: flag.step_id,
  };
}

/**
 * DCTA-143: a string of a step as the app prints it — itself, or, where the bank flagged it, its inert form. For the
 * sentences the card composes around a step (the held line, a cancel question, an account named on the money line),
 * so a flagged name is never printed bare anywhere the step's own row would print it inert.
 */
export function shownAs(flags: readonly DataFlag[], stepId: string, field: string, value: string): string {
  const flag = flags.find((f) => f.step_id === stepId && fieldOf(f.field) === field);
  return flag === undefined ? value : inertOf(flag, value).display;
}

/** What `describeBare` prints a string as: itself, or — flagged — the bank's inert form (DCTA-136). */
type Shown = (field: string, value: string) => string;

/**
 * What a step says, from the step alone. A computed amount (DCTA-120, QA2-07; trusted-confirmation-ux:
 * "computed amounts show their derivation") carries the bank's own signed explanation — "Half of step 1
 * (S$1,000.00)", "Statement balance on Platinum Card •••• 6012, read from the bank record: S$412.00." —
 * never a sum the card works out itself. DCTA-131: kept apart from `detail`, so the card can name it (a linked row's
 * description); it is drawn first in the opened row, joined to the detail with " · " (§F.2 row 15, DCTA-132).
 * `flags` (DCTA-136): the bank's flags beside the payload — each flagged string is printed in its inert form (`Inert`).
 */
/** DCTA-232: a card of protective actions only (the bank's unlock card) — no money moves, so it never reads as a payment. */
export function protectiveOnly(steps: readonly SignedStep[]): boolean {
  return steps.length > 0 && steps.every((s) => s.type === "protective_action");
}

/**
 * DCTA-209 (row 27l): `when` — a schedule card's words for when each payment goes ("goes at 09:00 on each date", …),
 * printed in the opened row in the place of "arrives now" / "pays now" (app/schedule-words.ts `detailTail`).
 */
export function describeStep(step: SignedStep, flags: readonly DataFlag[] = [], when?: readonly string[]): Row {
  const mine = new Map(flags.filter((f) => f.step_id === step.step_id).map((f) => [fieldOf(f.field), f] as const));
  const used: Inert[] = [];
  const shown: Shown = (field, value) => {
    const f = mine.get(field);
    if (f === undefined) return value;
    const inert = inertOf(f, value);
    // One entry per flagged string of the step (a name the row prints twice is one string), in the order printed.
    if (!used.some((u) => u.field === inert.field)) used.push(inert);
    return inert.display;
  };
  const bare = describeBare(step, shown, (field) => mine.has(field), when);
  const row = used.length === 0 ? bare : { ...bare, inert: used };
  const d = step.amount_derivation;
  if (d === undefined || d.kind === "fixed" || d.explanation.trim() === "") return row;
  return { ...row, derivation: d.explanation.trim() };
}

function describeBare(step: SignedStep, shown: Shown, flagged: (field: string) => boolean, when?: readonly string[]): Row {
  const m = money(step);
  const amount = m === undefined ? "" : formatMoney(m.value, m.currency);
  // DCTA-209: a schedule card's row says when each payment goes, never "now".
  const goes = (now: string): string => (when === undefined ? timing(step, now) : when.join(" · "));
  switch (step.type) {
    case "bill_payment": {
      const b = step.biller;
      const name = shown("biller/name", b.name);
      const ref = step.bill_reference === undefined ? undefined : shown("bill_reference", step.bill_reference);
      const from = shown("debit_account/display_name", step.debit_account.display_name);
      return {
        ic: "i-card",
        title: `Pay ${name}`,
        sub: `${ref === undefined ? "" : `${ref} · `}${lastDigits(b.masked_account)} · from ${from}`,
        // F12 (DCTA-132): a masked number as the sub-line prints it ("···6012"), never "•••• 6012" under "···6012".
        detail: `${name} ${lastDigits(b.masked_account)}${ref === undefined ? "" : ` · bill reference ${ref}`} · ${goes("pays now")}`,
        amount,
        name,
      };
    }
    case "funds_transfer": {
      const from = shown("debit_account/display_name", step.debit_account.display_name);
      const said = step.reference === undefined || step.reference === "" ? undefined : step.reference;
      const ref = said === undefined ? undefined : shown("reference", said);
      // A plain reference goes in the card's own quotes; a flagged one is printed in the bank's (its inert form).
      const quoted = ref === undefined ? undefined : flagged("reference") ? ref : `"${ref}"`;
      if (step.payee !== undefined && step.payee.source === "paynow_lookup") {
        // DCTA-120 (review P1-4, §F.2 6b-6d): a PayNow number paid once. The title is the name registered
        // with PayNow (as the directory masks it); the sub-line is the proxy — never the name a second
        // time — so the customer sees WHOSE number it is, and that nothing is saved.
        const p = step.payee;
        const registered = shown("payee/registered_name", p.registered_name);
        const proxy = `${PROXY[p.proxy_type] ?? "PayNow"} ${proxyTail(p.masked_proxy)}`;
        return {
          ic: "i-send",
          title: `Send to ${registered}`,
          sub: `${proxy}${ref === undefined ? "" : ` · ${ref}`} · from ${from}`,
          detail: [
            `Name registered with PayNow: ${registered}`,
            proxy,
            ...(quoted === undefined ? [] : [`reference ${quoted}`]),
            "paid once, not saved",
            goes("arrives now"),
          ].join(" · "),
          amount,
          name: registered,
        };
      }
      if (step.payee !== undefined) {
        const p = step.payee;
        const registered = shown("payee/registered_name", p.registered_name);
        const who = p.nickname === undefined ? registered : shown("payee/nickname", p.nickname);
        return {
          ic: "i-send",
          title: `Send to ${who}`,
          // proxyTail (DCTA-120): a UEN or a wallet keeps its unmasked end ("···345K", "···@mockwallet"), as digits always did.
          sub: `${registered} ${proxyTail(p.masked_proxy)}${ref === undefined ? "" : ` · ${ref}`} · from ${from}`,
          detail: [
            `${PROXY[p.proxy_type] ?? "PayNow"} ${proxyTail(p.masked_proxy)}`,
            `registered name ${registered}`,
            ...(quoted === undefined ? [] : [`reference ${quoted}`]),
            ...(p.is_new_payee ? ["new payee"] : []),
            goes("arrives now"),
          ].join(" · "),
          amount,
          name: who,
        };
      }
      const to = step.to_account;
      const toName = to === undefined ? "" : shown("to_account/display_name", to.display_name);
      return {
        ic: "i-send",
        title: `Move to ${toName}`,
        sub: `${to === undefined ? "" : `${lastDigits(to.masked_number)} · `}from ${from}`,
        detail: `${toName} ${to === undefined ? "" : lastDigits(to.masked_number)} · ${goes("arrives now")}`,
        amount,
        name: toName,
      };
    }
    case "securities_order": {
      const i = step.instrument;
      const name = shown("instrument/name", i.name);
      return {
        ic: "i-trend",
        title: `${step.side === "sell" ? "Sell" : "Buy"} ${name}`,
        // DCTA-83: a buy by amount is a maximum when the payload says so — never assumed.
        sub: `Market order${step.amount_is_max === true ? `, up to ${amount}` : ""} · from ${shown("settlement_account/display_name", step.settlement_account.display_name)}`,
        detail: [
          `${i.ticker} · ${i.exchange}`,
          `market order for ${step.amount_is_max === true ? "up to " : ""}${amount} at the next available price`,
          `quote ${formatMoney(step.quote.price, step.quote.currency)} at ${clock(step.quote.as_of)}`,
          ...(step.fx === undefined ? [] : [`${step.fx.pair} ${step.fx.rate}`]),
          ...(step.policy.outcome === "execute_now" ? [] : [timing(step, "")]),
        ].join(" · "),
        amount,
        name,
      };
    }
    case "payee_add": {
      // DCTA-124 (§F.2 13c): the row of the Check and sign card — the proxy masked to its end, then the
      // customer's own name for them. Nothing to expand: the card's lead names the payee (13b).
      const p = step.payee;
      const nickname = p.nickname === undefined ? undefined : shown("payee/nickname", p.nickname);
      return {
        ic: "i-plus",
        title: `${PROXY[p.proxy_type] ?? "PayNow"} ${proxyTail(p.masked_proxy)}`,
        sub: nickname === undefined ? "" : `Your name for them: ${nickname}`,
        detail: "",
        amount: "",
        name: nickname ?? shown("payee/registered_name", p.registered_name),
      };
    }
    case "protective_action": {
      // DCTA-232: the bank's unlock card takes the whole lock off (the step names one account only because the contract
      // needs one) — the bank's own words, never an account's name or a figure.
      if (step.action === "unlock_funds") return { ic: "i-unlock", title: UNLOCK_TITLE, sub: UNLOCK_TAKES, detail: "", amount: "", name: "your money" };
      const t = step.target;
      const target = shown("target/display_name", t.display_name);
      const title =
        step.action === "raise_limit"
          ? `Raise the daily limit${amount === "" ? "" : ` to ${amount}`}`
          : step.action === "unfreeze_card"
            ? `Unfreeze ${target}`
            : `Unlock ${target}`;
      return {
        ic: "i-lock",
        title,
        sub: `${target} ${lastDigits(t.masked_number)}`,
        detail: `${target} ${lastDigits(t.masked_number)}`,
        amount,
        name: target,
      };
    }
  }
}

/**
 * The lead of a card made on one of the bank's own screens (DCTA-124, §F.2 13b): what is signed, in
 * one line — "Add LIM R*** C*** PTE. LTD. as a payee". A conversation's card has none (its header is
 * the count and the total).
 */
export function surfaceLead(payload: SigningPayload, flags: readonly DataFlag[] = []): string | undefined {
  const add = leadStep(payload);
  if (add === undefined) return undefined;
  // DCTA-136 (F10): a registered name the bank flagged is printed in its inert form, as on the row.
  return `Add ${shownAs(flags, add.step_id, "payee/registered_name", add.payee.registered_name)} as a payee`;
}

/** DCTA-143: the lead's flagged name as an Inert entry — so the card sets it apart as data and can print it whole. */
export function surfaceLeadInert(payload: SigningPayload, flags: readonly DataFlag[] = []): Inert | undefined {
  const add = leadStep(payload);
  const flag = add === undefined ? undefined : flags.find((f) => f.step_id === add.step_id && fieldOf(f.field) === "payee/registered_name");
  return add === undefined || flag === undefined ? undefined : inertOf(flag, add.payee.registered_name);
}

function leadStep(payload: SigningPayload): Extract<SignedStep, { type: "payee_add" }> | undefined {
  if (payload.origin === undefined) return undefined;
  return payload.steps.find((s): s is Extract<SignedStep, { type: "payee_add" }> => s.type === "payee_add");
}

/** The account a step names as its source — an order's settlement account (either side), else its debit account. */
export interface DebitSource {
  readonly account: { readonly account_id: string; readonly display_name: string; readonly masked_number: string };
  /** The field the bank flags its name under (DCTA-143), e.g. "debit_account/display_name". */
  readonly field: string;
}

/** DCTA-143: the one place that knows which account a step takes from, and under which field its name is flagged. */
export function debitSource(step: SignedStep): DebitSource | undefined {
  if (step.type === "securities_order") return { account: step.settlement_account, field: "settlement_account/display_name" };
  if (step.type === "protective_action" || step.type === "payee_add") return undefined;
  return { account: step.debit_account, field: "debit_account/display_name" };
}

/** DCTA-143: a step's source account name as the app prints it — the bank's inert form where it flagged that name. */
export function sourceShown(step: SignedStep, flags: readonly DataFlag[]): string | undefined {
  const src = debitSource(step);
  return src === undefined ? undefined : shownAs(flags, step.step_id, src.field, src.account.display_name);
}

/**
 * DCTA-143: an account as the money line names it — its display name, or, where the bank flagged that name on a step
 * that takes money from it, the bank's inert form (the money line never prints bare what the row prints inert).
 */
export function accountShown(steps: readonly SignedStep[], flags: readonly DataFlag[], accountId: string, name: string): string {
  if (flags.length === 0) return name;
  for (const step of steps) {
    if (debitSource(step)?.account.account_id !== accountId) continue;
    const shown = sourceShown(step, flags);
    if (shown !== undefined && shown !== name) return shown;
  }
  return name;
}

/** The account each step takes money from, and how much, in order of first appearance. */
export function leaves(steps: readonly SignedStep[], flags: readonly DataFlag[] = []): { name: string; amount: string }[] {
  const order: string[] = [];
  const by = new Map<string, { name: string; values: string[]; currency: string }>();
  for (const step of steps) {
    // A sell takes nothing from an account: only what a step spends is on the money line.
    const src = step.type === "securities_order" && step.side !== "buy" ? undefined : debitSource(step);
    const m = money(step);
    if (src === undefined || m === undefined) continue;
    const key = `${src.account.account_id}:${m.currency}`;
    if (!by.has(key)) {
      order.push(key);
      by.set(key, { name: accountShown(steps, flags, src.account.account_id, src.account.display_name), values: [], currency: m.currency });
    }
    by.get(key)?.values.push(m.value);
  }
  return order.map((key) => {
    const e = by.get(key) as { name: string; values: string[]; currency: string };
    return { name: e.name, amount: `−${formatMoney(sumMoney(e.values), e.currency)}` };
  });
}


/** One account's line on the card under a /2 payload: every figure is the payload's own. */
export interface SignedLeave {
  readonly name: string;
  /** "−S$492.00"; "up to −S$2,000.00" when a BUY cap is in the total. */
  readonly amount: string;
  /** The executor's projection of this account's available balance, when the payload has one. */
  readonly before?: string;
  readonly after?: string;
}

/**
 * The money leaving each account, as a `dcta.signing-payload/2` signs it (contract §4.4):
 * `bundle.totals` in the bank's first-debit order, each with the `bundle.projected` entry
 * for the same account and currency. Nothing is added up here; an account the projection
 * does not cover gets no balance line rather than a computed one.
 */
export function signedLeaves(summary: BundleSummary, steps: readonly SignedStep[] = [], flags: readonly DataFlag[] = []): SignedLeave[] {
  return summary.totals.map((t) => {
    const p = summary.projected.find((x) => x.account_id === t.account.account_id && x.currency === t.total.currency);
    return {
      // DCTA-143: the bank flags display strings on the steps, not on the totals — a flagged account name on a step is
      // printed inert on the money line too.
      name: accountShown(steps, flags, t.account.account_id, t.account.display_name),
      amount: `${t.amount_is_max === true ? "up to " : ""}−${formatMoney(t.total.value, t.total.currency)}`,
      ...(p === undefined ? {} : { before: formatMoney(p.available_before, p.currency), after: formatMoney(p.available_after, p.currency) }),
    };
  });
}
